<script>
	import { base } from '$app/paths';
	import SeoHead from '../../../../common/SeoHead.svelte';
	import GuideImage from '../GuideImage.svelte';
	import guides from '../guides.json';
	let { data } = $props();
	let guide = $derived(data.guide);
	const origin = `https://tools.d-cine.net${base}`;
	const screenshots = {
		'device-certs': [
			{
				src: 'device-search-screen.webp',
				caption: 'Actual screen: the Device Certs search and column filters.',
				alt: 'KdmGen Device Certs screen showing the approved-device, software-device and OpenTDL tabs above the searchable certificate table.'
			}
		],
		'register-dcp-cpl': [
			{
				src: 'backend-certificate-screen.webp',
				caption: 'Actual screen: download the public backend recipient certificate.',
				alt: 'Backend DKDM Target Certificate panel with creation instructions, Download Public Certificate and Cert Details buttons.'
			},
			{
				src: 'batch-registration-screen.webp',
				caption: 'Actual screen: the batch registration table before compositions are added.',
				alt: 'Register CPLs and DKDMs panel with Add CPL, Add CPLs in a DCP and the per-composition DKDM status columns.'
			},
			{
				src: 'single-cpl-screen.webp',
				caption: 'Actual screen: the alternative single-CPL upload workflow.',
				alt: 'Upload CPL panel offering Search local DB for CPL and Choose file.'
			}
		],
		'generate-kdm': [
			{
				src: 'target-cpls-screen.webp',
				caption: 'Actual screen: Target CPLs starts with an empty selection.',
				alt: 'Target CPLs panel with My CPLs visibility, Add CPL and Add via DCP controls.'
			},
			{
				src: 'validate-screen.webp',
				caption: 'Actual screen: validation and creation are separate actions.',
				alt: 'Review and Create panel with Validate Setup and Create Quick KDM buttons.'
			},
			{
				src: 'booking-automation-screen.webp',
				caption: 'Actual screen: managed bookings offer regeneration and email delivery options.',
				alt: 'Booking Automation controls for regenerating KDMs after OpenTDL device changes and delivering them to facility KDM addresses.'
			}
		],
		'facilities-opentdl': [
			{
				src: 'facility-filters-screen.webp',
				caption: 'Actual screen: facility search filters appear beneath the column headings.',
				alt: 'Facility table column headings and search inputs used to locate an OpenTDL cinema.'
			}
		]
	};
	let structuredData = $derived(
		JSON.stringify({
			'@context': 'https://schema.org',
			'@graph': [
				{
					'@type': 'TechArticle',
					headline: guide.heading,
					description: guide.description,
					url: `${origin}/kdmgen/help/${guide.slug}/`,
					inLanguage: 'en',
					image: `${origin}/kdmgen-help/${guide.slug}-${guide.steps[0].id}.webp`,
					author: { '@type': 'Organization', name: 'D-Cine.net', url: 'https://tools.d-cine.net' },
					publisher: { '@type': 'Organization', name: 'D-Cine.net' },
					articleSection: 'KdmGen user manuals',
					proficiencyLevel: 'Beginner',
					mainEntityOfPage: `${origin}/kdmgen/help/${guide.slug}/`
				},
				{
					'@type': 'BreadcrumbList',
					itemListElement: [
						{ '@type': 'ListItem', position: 1, name: 'KdmGen', item: `${origin}/kdmgen/welcome/` },
						{
							'@type': 'ListItem',
							position: 2,
							name: 'Walk Throughs/Help',
							item: `${origin}/kdmgen/help/`
						},
						{
							'@type': 'ListItem',
							position: 3,
							name: guide.title,
							item: `${origin}/kdmgen/help/${guide.slug}/`
						}
					]
				}
			]
		}).replace(/</g, '\\u003c')
	);
</script>

<SeoHead
	title={`${guide.title} walkthrough: ${guide.heading}`}
	description={guide.description}
	path={`/kdmgen/help/${guide.slug}/`}
	image={`/kdmgen-help/${guide.slug}-${guide.steps[0].id}.webp`}
	type="article"
/>
<svelte:head>{@html `<script type="application/ld+json">${structuredData}</script>`}</svelte:head>

<main class="help-content">
	<article>
		<header class="guide-header">
			<nav class="breadcrumbs" aria-label="Breadcrumb">
				<a href={`${base}/kdmgen/welcome/`}>KdmGen</a><span aria-hidden="true">/</span><a
					href={`${base}/kdmgen/help/`}>Walk Throughs/Help</a
				><span aria-hidden="true">/</span><span>{guide.title}</span>
			</nav>
			<p class="guide-kicker">{guide.time} · {guide.steps.length} illustrated steps</p>
			<h1>{guide.heading}</h1>
			<p class="guide-lead">{guide.intro}</p>
			<a class="open-tool" href={`${base}/kdmgen/${guide.tool}/`} target="_blank" rel="noopener"
				><i class="bx bx-link-external" aria-hidden="true"></i> Open {guide.title}</a
			>
		</header>
		<div class="guide-layout">
			<aside class="guide-toc">
				<nav aria-label="On this page">
					<strong>On this page</strong><a href="#before-you-begin">Before you begin</a><a
						href="#screen-reference">Screen reference</a
					>{#each guide.steps as step, i}<a href={`#${step.id}`}
							><span>{i + 1}.</span> {step.title}</a
						>{/each}<a href="#troubleshooting">Troubleshooting</a>
				</nav>
			</aside>
			<div class="guide-body">
				<section id="before-you-begin" class="before-you-begin">
					<h2>Before you begin</h2>
					<ul>
						{#each guide.prerequisites as item}<li>{item}</li>{/each}
					</ul>
				</section>
				<section id="screen-reference" class="screen-reference">
					<h2>Recognise the screen</h2>
					<p>
						These screenshots show the actual tool. The numbered steps below use labelled examples
						to explain the selections and results.
					</p>
					{#each screenshots[guide.slug] as screenshot, i}<GuideImage
							{...screenshot}
							eager={i === 0}
						/>{/each}
				</section>
				{#each guide.steps as step, i}
					<section id={step.id} class="guide-step">
						<div class="step-heading">
							<span class="step-number" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
							<h2>{step.title}</h2>
						</div>
						{#each step.body as paragraph}<p>{paragraph}</p>{/each}
						<GuideImage
							src={`${guide.slug}-${step.id}.webp`}
							alt={`${step.visual.title}. ${step.visual.note} ${step.visual.callouts.join(' ')}`}
							caption={`Step ${i + 1}: ${step.visual.title}. Illustrated example using sample data.`}
						/>
						<p class="step-result">
							<i class="bx bx-check-circle" aria-hidden="true"></i><span
								><strong>Expected result:</strong> {step.outcome}</span
							>
						</p>
					</section>
				{/each}
				<section id="troubleshooting" class="troubleshooting">
					<h2>Troubleshooting</h2>
					{#each guide.troubleshooting as item}<details>
							<summary>{item.question}</summary>
							<p>{item.answer}</p>
						</details>{/each}
				</section>
				<aside class="help-support">
					<strong>Still need assistance?</strong>
					<p>
						Email <a href="mailto:support@d-cine.net">support@d-cine.net</a> with the tool name, the
						relevant CPL UUID or facility identity and the exact error message.
					</p>
				</aside>
			</div>
		</div>
	</article>
	<nav class="related-guides" aria-label="Related walkthroughs">
		<h2>Continue with another guide</h2>
		<div>
			{#each guides.filter((item) => item.slug !== guide.slug) as item}<a
					href={`${base}/kdmgen/help/${item.slug}/`}
					>{item.title}<i class="bx bx-right-arrow-alt" aria-hidden="true"></i></a
				>{/each}
		</div>
	</nav>
</main>
